import { NON_EXISTING_USER_NAME } from "@overmap-ai/common"
import type { FormIdentifier, Stored, User } from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"

import type { FormSubmissionData } from "../../typings"
import { getUserDisplayLabel } from "../../utils/users"
import { combineColumnDefs, CSVExporter } from "./CSVExporter"
import type { CSVColumnDef } from "./typings"
import { getCSVColumnDefsOfFieldSchema } from "./utils/fields"
import { getCSVColumnDefsOfIdentifiers } from "./utils/identifiers"

interface FormSubmissionDataCsvExporterConfig {
	formFieldSchema: FieldSchema
	formIdentifiers: Stored<FormIdentifier>[]
	users: Stored<User>[]
}

export const getFormSubmissionDataCsvExporter = (
	data: FormSubmissionData[],
	config: FormSubmissionDataCsvExporterConfig,
) => {
	const { formFieldSchema, formIdentifiers, users } = config

	const usersById = Object.fromEntries(users.map((user) => [user.id, user]))

	const baseColumns: CSVColumnDef<FormSubmissionData>[] = [
		{ id: "submitted_at", header: "Submitted at", accessorFn: (row) => row.formSubmission.submitted_at },
		{
			id: "created_by",
			header: "Creator",
			accessorFn: (row) => {
				if (!row.formSubmission.created_by) return NON_EXISTING_USER_NAME
				const user = usersById[row.formSubmission.created_by]
				return getUserDisplayLabel(user)
			},
		},
	]

	const formIdentifierColumns = getCSVColumnDefsOfIdentifiers<Stored<FormIdentifier>, FormSubmissionData>(
		formIdentifiers,
		(identifier) => (row) => row.formIdentifierValues[identifier.uuid]?.value,
	)

	const formFieldColumns = getCSVColumnDefsOfFieldSchema<FormSubmissionData>(
		formFieldSchema,
		(row) => row.formSubmissionFieldValues?.values,
	)

	return new CSVExporter({
		columns: combineColumnDefs(baseColumns, formIdentifierColumns, formFieldColumns),
		data: data,
	})
}
